/**
 * LtiLaunchLanding — Phase 2.0
 * Where Canvas-launched users land after the OIDC + id_token flow.
 *
 * Reads ?lti=<launch_id> from the URL, exchanges it for the launch context
 * (course, user, roles, AGS endpoints), and either:
 *   - greets the launching user with the Canvas course context, and
 *   - offers a one-click jump into the appropriate CourseForge view
 *     (student dashboard, teacher dashboard, or developer console).
 *
 * Long-term this is where we'd auto-link the LTI user to a local user
 * record and create a session. For now it's the proof-of-flow surface so
 * we can demo "Canvas → CourseForge" in <2s without a second login.
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getLtiLaunchContext } from '../lib/apiClient';

const isInstructor = (roles = []) =>
  roles.some((r) => /Instructor|TeachingAssistant|ContentDeveloper|Administrator/.test(r));

export default function LtiLaunchLanding() {
  const [params] = useSearchParams();
  const launchId = params.get('lti');
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!launchId) {
      setErr('Missing launch token. Open this page from Canvas.');
      return;
    }
    getLtiLaunchContext(launchId)
      .then(setCtx)
      .catch((e) => setErr(e?.response?.data?.detail || String(e)));
  }, [launchId]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="card p-6 max-w-md text-center" data-testid="lti-launch-error">
          <h2 className="text-lg font-bold text-red-700 mb-2">Canvas launch failed</h2>
          <p className="text-sm text-gray-700">{err}</p>
          <Link to="/login" className="inline-block mt-4 text-sm text-emerald-700 underline">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  if (!ctx) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-sm text-gray-500" data-testid="lti-launch-loading">Loading Canvas launch…</p>
      </div>
    );
  }

  const courseName = ctx.context?.title || ctx.context?.label || 'your course';
  const teacher = isInstructor(ctx.roles);
  const target = teacher ? '/teacher' : '/student';

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center p-6" data-testid="lti-launch-landing">
      <div className="card p-8 max-w-xl w-full">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <p className="text-xs uppercase tracking-widest font-semibold text-emerald-700">
            Canvas → CourseForge
          </p>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          Welcome{ctx.user?.name ? `, ${ctx.user.name}` : ''} 👋
        </h1>
        <p className="text-sm text-gray-600 mb-5">
          Launched from <strong>{courseName}</strong> via LTI 1.3 — no additional login required.
        </p>

        <div className="grid grid-cols-2 gap-3 text-xs mb-5">
          <div className="bg-gray-50 rounded p-3 border border-gray-200">
            <p className="text-gray-500 uppercase tracking-wide font-semibold">Role</p>
            <p className="font-mono text-gray-900 break-all">
              {teacher ? 'Instructor / Staff' : 'Learner'}
            </p>
          </div>
          <div className="bg-gray-50 rounded p-3 border border-gray-200">
            <p className="text-gray-500 uppercase tracking-wide font-semibold">Message</p>
            <p className="font-mono text-gray-900">
              {ctx.is_deep_linking ? 'Deep Linking' : 'Resource Link'}
            </p>
          </div>
          <div className="bg-gray-50 rounded p-3 border border-gray-200 col-span-2">
            <p className="text-gray-500 uppercase tracking-wide font-semibold">Canvas Context</p>
            <p className="font-mono text-gray-900 break-all">
              {ctx.context?.id || '(no context_id)'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to={target}
            className="flex-1 text-center px-4 py-2.5 rounded bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
            data-testid="lti-launch-continue"
          >
            Continue to CourseForge →
          </Link>
        </div>
        <p className="text-[11px] text-gray-400 mt-4">
          Launch id: <code>{launchId}</code> · expires in 15 min
        </p>
      </div>
    </div>
  );
}
